
import { useState, useEffect } from "react"

/**
 * Enhanced hook to detect iOS devices and PWA mode
 * @returns object with iOS detection, PWA status, and standalone mode
 */
export function useIOSDetection() {
  const [isIOS, setIsIOS] = useState<boolean>(false)
  const [isPWA, setIsPWA] = useState<boolean>(false)
  const [isStandalone, setIsStandalone] = useState<boolean>(false)
  
  useEffect(() => {
    // Check if device is iOS (iPhone, iPad, iPod)
    const checkIsIOS = () => {
      // More reliable iOS detection methods
      const userAgent = navigator.userAgent || "";
      const isAppleDevice = /iPhone|iPad|iPod|Mac/.test(userAgent);
      const hasIosSafariFeatures = 'ontouchend' in document;
      
      // iOS 13+ iPad detection (reports as Mac)
      const iPadOS = navigator.platform === 'MacIntel' && 
                     navigator.maxTouchPoints > 1;
      
      // iOS-specific CSS property detection (most reliable method)
      const supportsWebkitOverflowScrolling = 
        typeof document !== 'undefined' && 
        'WebkitOverflowScrolling' in document.documentElement.style;
                     
      // Additional iOS-specific features check
      const supportsTouchCallout = 
        typeof document !== 'undefined' &&
        'webkitTouchCallout' in document.documentElement.style;
        
      // Check if we're running in Safari on iOS by feature detection
      const isIOSSafari = 
        'standalone' in window.navigator &&
        (/iphone|ipod|ipad/i).test(userAgent.toLowerCase());
      
      // Detect PWA mode (bookmarked to home screen)
      const isPWAMode = 
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://');
      
      const iosDetected = (isAppleDevice && hasIosSafariFeatures) || 
        iPadOS || 
        supportsWebkitOverflowScrolling ||
        supportsTouchCallout ||
        isIOSSafari;
      
      // Standalone detection for installed PWA
      const standaloneDetected = 
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;
      
      setIsIOS(iosDetected);
      setIsPWA(isPWAMode);
      setIsStandalone(standaloneDetected);
    }
    
    checkIsIOS();
    
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkIsIOS();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    }
    
    window.addEventListener('resize', checkIsIOS);
    return () => {
      window.removeEventListener('resize', checkIsIOS);
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      }
    };
  }, []);
  
  return { isIOS, isPWA, isStandalone };
}
